import { Component } from '@verndale/core';
import Swiper, { Navigation, Keyboard } from 'swiper';
import 'swiper/css/bundle';
import figure from './figure';
import {
  disableBodyScroll,
  clearAllBodyScrollLocks,
  BodyScrollOptions
} from 'body-scroll-lock';
import { FocusTrap, createFocusTrap } from 'focus-trap';
import { ExtendedWindowType } from '../helpers/global-types';

class MediaGallery extends Component {
  private gallerySwiper: Swiper | null = null;
  private galleryItems: HTMLElement[] | undefined;
  private resizeTimeout: NodeJS.Timeout | null | undefined;
  private galleryIndex: number | undefined;
  private galleryItemsLastIndex: number | undefined;
  private videoFigure: figure | undefined;
  private firstSlideFlag = true;
  private focusTrap: FocusTrap | undefined;

  constructor(el: HTMLElement) {
    super(el);
    this.videoFigure = new figure(this.dom.figureContainer as HTMLElement);
    this.gallerySwiper = null;
    this.initSwiper();
    this.handleResize();
    this.updateSwiper();
    this.setPreviewThumbnails();
  }

  setupDefaults() {
    this.dom = {
      el: this.el,
      swiper: this.el.querySelector<HTMLElement>(
        '.media-gallery__swiper-container'
      ),
      modal: this.el.querySelector<HTMLElement>('#media-gallery__modal'),
      modalImage: this.el.querySelector<HTMLElement>(
        '.media-gallery__modal-image'
      ),
      modalCaption: this.el.querySelector<HTMLElement>(
        '.media-gallery__modal-caption'
      ),
      nextArrow: this.el.querySelector<HTMLElement>('.swiper-button-next'),
      prevArrow: this.el.querySelector<HTMLElement>('.swiper-button-prev'),
      galleryItems: this.el.querySelectorAll('.gallery-item'),
      galleryVideoItems: this.el.querySelectorAll(
        '.media-gallery__video-item .figure__media-button'
      ),
      closeBtn: this.el.querySelector<HTMLElement>('.media-gallery__close'),
      imagePreviewRow: this.el.querySelector<HTMLElement>(
        '.media-gallery__image-preview-row'
      ),
      imagePreviews: this.el.querySelectorAll<HTMLElement>(
        '.media-gallery__image-preview'
      ),
      figureContainer: this.el.querySelector<HTMLElement>(
        '.figure__container--modal'
      ),
      swiperSlides: this.el.querySelectorAll<HTMLElement>('.swiper-slide')
    };

    this.setGalleryItems();
  }

  addListeners() {
    //Show modal on gallery-item click
    this.galleryItems?.forEach((item, index) => {
      item.addEventListener('click', () => {
        this.showItemInModal(item, index);
      });
    });

    // Close modal on close button click
    (this.dom.closeBtn as HTMLElement).addEventListener('click', () => {
      this.handleCloseModal();
    });

    // Close modal when pressing ESC key
    document.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        this.handleCloseModal();
      }
    });

    // Close modal on overlay click
    if (this.dom.modal) {
      (this.dom.modal as HTMLElement).addEventListener(
        'click',
        (event: MouseEvent) => {
          if (event.target === this.dom.modal) {
            this.handleCloseModal();
          }
        }
      );
    }

    //Close the modal when the user clicks on .media-gallery__modal-content
    const modalContent = this.el.querySelector(
      '.media-gallery__modal-content'
    ) as HTMLElement;
    modalContent?.addEventListener('click', () => {
      this.handleCloseModal();
    });

    // Image Preview Functionality
    (this.dom.imagePreviews as NodeListOf<HTMLElement>).forEach(
      (preview, index) => {
        this.handlePreviewImage(preview, index);
      }
    );

    // Handle next and prev arrow clicks
    (this.dom.nextArrow as HTMLButtonElement)?.addEventListener(
      'click',
      this.handleNextArrowClick.bind(this)
    );

    (this.dom.prevArrow as HTMLButtonElement)?.addEventListener(
      'click',
      this.handlePrevArrowClick.bind(this)
    );
  }

  initSwiper() {
    Swiper.use([Navigation, Keyboard]);
    this.setGalleryItems();
    let desktopOffSetWidth = 0;
    if (this.galleryItems) {
      if (this.galleryItems.length < 6) {
        desktopOffSetWidth = (this.dom.swiper as HTMLElement).offsetWidth * -1;
      }
      if (this.galleryItems.length == 6) {
        desktopOffSetWidth = -20;
      }

      if (this.galleryItems.length >= 7) {
        if (this.galleryItems.length % 2 == 0) {
          desktopOffSetWidth =
            ((this.galleryItems?.length - 4) / 2) *
              ((this.dom.swiper as HTMLElement).offsetWidth / 4) *
              -1 -
            this.galleryItems?.length * 4;
        } else {
          desktopOffSetWidth =
            ((this.galleryItems?.length - 3) / 2) *
              ((this.dom.swiper as HTMLElement).offsetWidth / 4) *
              -1 -
            this.galleryItems?.length * 4;
        }
      }
    }

    const slides = this.dom.swiperSlides as NodeListOf<HTMLElement>;

    const hideNotActiveSlides = () => {
      slides.forEach(slide => {
        const button = slide.querySelector('button') as HTMLButtonElement;

        if (slide.classList.contains('swiper-slide-visible')) {
          button.tabIndex = 0;
        } else {
          button.tabIndex = -1;
        }
      });
    };

    this.gallerySwiper = new Swiper(this.dom.swiper as HTMLElement, {
      slidesPerView: 1,
      spaceBetween: 8,
      roundLengths: true,
      navigation: {
        nextEl: this.dom.nextArrow as HTMLElement,
        prevEl: this.dom.prevArrow as HTMLElement
      },
      watchOverflow: true,
      breakpoints: {
        [768]: {
          slidesPerView: 4,
          slidesPerGroup: 1,
          spaceBetween: 32,
          grid: {
            rows: 2
          },
          slidesOffsetAfter: desktopOffSetWidth,
          watchSlidesProgress: true
        }
      },
      keyboard: {
        enabled: true,
        onlyInViewport: true
      },
      on: {
        afterInit: () => {
          setTimeout(() => {
            hideNotActiveSlides();
          }, 250);
        },
        slideChangeTransitionEnd: hideNotActiveSlides
      }
    });
  }

  updateSwiper() {
    if (this.gallerySwiper) {
      this.gallerySwiper.destroy?.();

      //then reinitialize the swiper gallery
      this.initSwiper();
    }
    if (window.innerWidth >= 768) {
      const nodeList = this.dom.galleryItems as NodeListOf<HTMLElement>;
      const firstElement = nodeList[0] as HTMLElement;
      const secondElement = nodeList[1] as HTMLElement;
      const secondElementWidth = secondElement.clientWidth;
      const firstElementDesiredWidth = secondElementWidth * 2 + 32;
      const sliderElementDesiredHeight = secondElementWidth * 2 + 40;
      const desiredSliderWidth = `${sliderElementDesiredHeight}px`;

      //Get the parent element of the firstElement
      const parentElement = firstElement.parentNode as HTMLElement;

      //Get slider element
      const sliderElement = parentElement.parentNode as HTMLElement;
      sliderElement.style.height = desiredSliderWidth;

      // change the parent node width to the FirstElementDesiredWidth
      const desiredWidth = `${firstElementDesiredWidth}px`;

      parentElement.style.width = desiredWidth;
      parentElement.style.height = desiredWidth;
    }
  }

  showItemInModal(item: HTMLElement, index: number) {
    // if the this.el contains the class experience-editor then return
    if (this.el.classList.contains('experience-editor')) {
      return;
    }
    const modal = this.dom.modal as HTMLElement;
    const modalImage = this.dom.modalImage as HTMLImageElement;
    const modalCaption = this.dom.modalCaption as HTMLElement;
    const videoModalBackdrop = modal.querySelector(
      '.video-modal__backdrop'
    ) as HTMLElement;
    this.galleryIndex = index;
    this.setGalleryItems();

    document.body.classList.add('media-gallery__modal-opened');

    if (this.galleryItems) {
      this.galleryItemsLastIndex = this.galleryItems.length - 1;
    }

    if (modal) {
      const options = {
        reserveScrollBarGap: true,
        allowTouchMove: (el: HTMLElement) => {
          return el.closest('.media-gallery__modal') !== null;
        }
      } as BodyScrollOptions;

      this.focusTrap = createFocusTrap(modal, {
        clickOutsideDeactivates: true,
        returnFocusOnDeactivate: true,
        onActivate() {
          disableBodyScroll(modal, options);
        },
        onDeactivate() {
          clearAllBodyScrollLocks();
        }
      });

      modal.classList.remove('media-gallery__modal-close');
      modal.classList.add('media-gallery__modal-open');

      this.focusTrap?.activate();

      const parent = item.closest(
        '.media-gallery__swiper-slide'
      ) as HTMLElement;

      const figure = parent.querySelector('.figure') as HTMLElement;

      if (figure) {
        // Is a video element
        modalImage.classList.add('media-gallery__modal-image--disabled');
        const trigger = figure;
        videoModalBackdrop?.classList.remove(
          'media-gallery__modal-video--disabled'
        );
        this.setVideoModalData(trigger);
      } else {
        // Is an image element
        videoModalBackdrop?.classList.add(
          'media-gallery__modal-video--disabled'
        );
        modalImage.classList.remove('media-gallery__modal-image--disabled');
        const image = parent.querySelector('img') as HTMLImageElement;
        modalImage.src = image.src;
      }

      const caption = parent.querySelector('.media-gallery__image-caption');

      modalCaption.textContent = caption?.textContent ?? null;
      (this.dom.nextArrow as HTMLElement).classList.remove(
        'swiper-button-lock'
      );
      (this.dom.prevArrow as HTMLElement).classList.remove(
        'swiper-button-lock'
      );

      if (index === 0) {
        (this.dom.prevArrow as HTMLElement).classList.add(
          'swiper-button-disabled'
        );
      } else {
        (this.dom.prevArrow as HTMLElement).classList.remove(
          'swiper-button-disabled'
        );
      }

      //set focus to modal, element .media-gallery__modal-image
      modalImage.focus();

      //add the class .arrow-over-modal to the next and prev buttons
      (this.dom.nextArrow as HTMLElement).classList.add('arrow-over-modal');
      (this.dom.prevArrow as HTMLElement).classList.add('arrow-over-modal');
    }

    if (
      (this.galleryIndex as number) === (this.galleryItemsLastIndex as number)
    ) {
      (this.dom.nextArrow as HTMLElement).classList.add(
        'swiper-button-disabled'
      );
    } else {
      (this.dom.nextArrow as HTMLElement).classList.remove(
        'swiper-button-disabled'
      );
      (this.dom.nextArrow as HTMLElement).removeAttribute('disabled');
      (this.dom.prevArrow as HTMLElement).removeAttribute('disabled');
    }

    (window as ExtendedWindowType).dataLayer.push({
      event: 'media_gallery',
      event_category: 'project profiles',
      event_action: 'media gallery open',
      event_label: '',
      event_attributes: {
        gallery_type: 'popover',
        thumbnail_count: index + 1
      }
    });
  }

  setVideoModalData(trigger: HTMLElement | null) {
    const videoId = trigger?.getAttribute('data-url');
    if (trigger) {
      const videoDataset = trigger?.dataset as DOMStringMap;
      videoDataset.videoPlayerPlay = videoDataset.videoPlayerPlay?.replace(
        '{videoName}',
        'other'
      );
    }
    this.videoFigure?.handleReplaceId(videoId as string);
  }

  setGalleryItems() {
    const figureGalleryItems = Array.from(
      this.dom.swiperSlides as NodeListOf<HTMLElement>
    );

    this.galleryItems = figureGalleryItems;
    return this.galleryItems;
  }

  setNoActivePreview() {
    const activePreview = this.el.querySelector(
      '.media-gallery__image-preview.active'
    ) as HTMLElement;
    activePreview?.classList.remove('active');
  }

  setPreviewThumbnails() {
    this.setGalleryItems();

    const imagePreviewRow = this.dom.imagePreviewRow as HTMLElement;
    if (this.galleryItems) {
      this.galleryItems?.forEach((item, index) => {
        const div = document.createElement('div');
        const preview = document.createElement('img');
        div.classList.add('media-gallery__image-preview');
        div.setAttribute('data-index', index.toString());

        if (item.classList.contains('figure__media-button')) {
          // Is a video element
          const videoThumbnail =
            item?.previousElementSibling?.querySelector('img');
          preview.src = (videoThumbnail as HTMLImageElement).src;
          preview.alt = (videoThumbnail as HTMLImageElement).alt;
          preview.loading = 'lazy';
          preview.width = 65;
          preview.height = 65;
          div.classList.add('media-gallery__image-preview--video');
          preview.setAttribute('data-index', index.toString());
        } else {
          // Is an image element
          preview.src = (item as HTMLImageElement).querySelector('img')
            ?.src as string;
          preview.alt = (item as HTMLImageElement).querySelector('img')
            ?.alt as string;
          preview.loading = 'lazy';
          preview.width = 65;
          preview.height = 65;
          preview.setAttribute('data-index', index.toString());
        }

        if (index === 0) {
          //add the active class to the first preview
          div.classList.add('active');
        }

        div.appendChild(preview);
        imagePreviewRow.appendChild(div);
      });
    }

    this.galleryItems?.forEach((_item, index) => {
      const preview = imagePreviewRow.children[index] as HTMLElement;
      this.handlePreviewImage(preview, index);
    });
  }

  handleResize() {
    window.addEventListener('resize', () => {
      if (this.resizeTimeout) {
        clearTimeout(this.resizeTimeout);
      }

      this.resizeTimeout = setTimeout(() => {
        this.updateSwiper();
      }, 300);
    });
  }

  handlePreviewImage(preview: HTMLElement, index: number) {
    preview.addEventListener('click', () => {
      if (!preview.classList.contains('active')) {
        const activePreview = this.el.querySelector(
          '.media-gallery__image-preview.active'
        ) as HTMLElement;
        activePreview?.classList.remove('active');
        preview.classList.add('active');
        if (this.gallerySwiper) {
          this.gallerySwiper.slideTo(index);
        }
      }
    });
  }

  handleCloseModal() {
    this.focusTrap?.deactivate();
    this.pauseVideoInSlide(this.galleryIndex);
    this.setGalleryItems();
    if (this.galleryItems) {
      this.galleryItemsLastIndex = this.galleryItems.length - 1;
    }

    if (this.dom.modal) {
      (this.dom.modal as HTMLElement).classList.remove(
        'media-gallery__modal-open'
      );
      (this.dom.modal as HTMLElement).classList.add(
        'media-gallery__modal-close'
      );
    }

    this.gallerySwiper?.enable();
    this.updateSwiper();

    const imagePreviews = this.el.querySelectorAll(
      '.media-gallery__image-preview'
    );
    //remove the active class from all previews
    imagePreviews.forEach(preview => {
      preview.classList.remove('active');
    });
    //add the active class to the first preview
    const firstPreview = imagePreviews[0] as HTMLElement;
    firstPreview.classList.add('active');
    this.scrollToActivePreview();
    this.firstSlideFlag = true;

    // Remove the 'media-gallery__modal-opened' class from the body to enable scrolling
    document.body.classList.remove('media-gallery__modal-opened');

    //remove the class .arrow-over-modal to the next and prev buttons
    (this.dom.nextArrow as HTMLElement).classList.remove('arrow-over-modal');
    (this.dom.prevArrow as HTMLElement).classList.remove('arrow-over-modal');

    (window as ExtendedWindowType).dataLayer.push({
      event: 'media_gallery',
      event_category: 'project profiles',
      event_action: 'media gallery close',
      event_label: '',
      event_attributes: {
        gallery_type: 'popover',
        thumbnail_count: (this.galleryIndex as number) + 1
      }
    });
  }

  handleNextArrowClick() {
    const modal = this.dom.modal as HTMLElement;
    this.setGalleryItems();
    const currentSlideIndex = this.gallerySwiper?.activeIndex;

    if (modal.classList.contains('media-gallery__modal-open')) {
      if (this.gallerySwiper) {
        if (this.galleryItems) {
          const nextImage =
            this.galleryItems?.[(this.galleryIndex as number) + 1];
          if (nextImage) {
            // Pause the video in the current active slide
            this.pauseVideoInSlide(this.galleryIndex);

            this.showItemInModal(nextImage, (this.galleryIndex as number) + 1);
            this.gallerySwiper.disable();
            (this.dom.nextArrow as HTMLElement).classList.remove(
              'swiper-button-lock'
            );
            (this.dom.prevArrow as HTMLElement).classList.remove(
              'swiper-button-lock'
            );
          } else {
            this.handleCloseModal();
          }
        }
      }

      (window as ExtendedWindowType).dataLayer.push({
        event: 'media_gallery',
        event_category: 'project profiles',
        event_action: 'media gallery arrow click',
        event_label: 'next',
        event_attributes: {
          gallery_type: 'popover',
          thumbnail_count: (this.galleryIndex as number) + 1
        }
      });
    } else {
      //if is tablet and above
      if (window.innerWidth >= 768) {
        //if firstTimeFlag is true then set it to false
        if (this.firstSlideFlag) {
          this.firstSlideFlag = false;
          //swiper slide to next
          this.gallerySwiper?.slideNext();
        }
      }

      const imagePreviews = this.el.querySelectorAll(
        '.media-gallery__image-preview'
      );
      const nextIndex = (currentSlideIndex as number) + 1;
      imagePreviews.forEach(preview => {
        if (preview.getAttribute('data-index') === nextIndex?.toString()) {
          const activePreview = this.el.querySelector(
            '.media-gallery__image-preview.active'
          ) as HTMLElement;
          activePreview?.classList.remove('active');
          //add the active class to matched preview

          preview.classList.add('active');
          this.scrollToActivePreview();
        }
      });

      (window as ExtendedWindowType).dataLayer.push({
        event: 'media_gallery',
        event_category: 'project profiles',
        event_action: 'media gallery arrow click:next',
        event_label: '',
        event_attributes: {
          gallery_type: 'embedded',
          thumbnail_count: ''
        }
      });
    }
  }

  handlePrevArrowClick() {
    const modal = this.dom.modal as HTMLElement;
    this.setGalleryItems();

    if (modal.classList.contains('media-gallery__modal-open')) {
      if (this.gallerySwiper) {
        if (this.galleryItems) {
          const prevImage =
            this.galleryItems?.[(this.galleryIndex as number) - 1];
          if (prevImage) {
            // Pause the video in the current active slide
            this.pauseVideoInSlide(this.galleryIndex);

            this.showItemInModal(prevImage, (this.galleryIndex as number) - 1);
            this.gallerySwiper.disable();
            (this.dom.nextArrow as HTMLElement).classList.remove(
              'swiper-button-lock'
            );
            (this.dom.prevArrow as HTMLElement).classList.remove(
              'swiper-button-lock'
            );
          } else {
            this.handleCloseModal();
          }
        }
      }

      (window as ExtendedWindowType).dataLayer.push({
        event: 'media_gallery',
        event_category: 'project profiles',
        event_action: 'media gallery arrow click: previous',
        event_label: '',
        event_attributes: {
          gallery_type: 'popover',
          thumbnail_count: (this.galleryIndex as number) + 1
        }
      });
    } else {
      const imagePreviews = this.el.querySelectorAll(
        '.media-gallery__image-preview'
      );
      const currentSlideIndex = this.gallerySwiper?.activeIndex;
      const prevIndex = (currentSlideIndex as number) - 1;
      imagePreviews.forEach(preview => {
        if (preview.getAttribute('data-index') === prevIndex?.toString()) {
          const activePreview = this.el.querySelector(
            '.media-gallery__image-preview.active'
          ) as HTMLElement;

          activePreview?.classList.remove('active');

          //add the active class to matched preview
          preview.classList.add('active');
          this.scrollToActivePreview();
          this.firstSlideFlag = true;
        }
      });

      if (window.innerWidth >= 768) {
        if (currentSlideIndex === 2) {
          this.firstSlideFlag = true;
          this.gallerySwiper?.slidePrev();
        }
      }

      (window as ExtendedWindowType).dataLayer.push({
        event: 'media_gallery',
        event_category: 'project profiles',
        event_action: 'media gallery arrow click:previous',
        event_label: '',
        event_attributes: {
          gallery_type: 'embedded',
          thumbnail_count: ''
        }
      });
    }
  }

  scrollToActivePreview() {
    const activePreview = this.el.querySelector(
      '.media-gallery__image-preview.active'
    ) as HTMLElement;
    const previewRow = this.el.querySelector(
      '.media-gallery__image-preview-row'
    ) as HTMLElement;
    const activePreviewWidth = activePreview.clientWidth + 20;
    const activePreviewIndex = activePreview.getAttribute('data-index');
    const scrollAmount =
      activePreviewWidth * (parseInt(activePreviewIndex as string) || 0);

    previewRow.scrollTo({ left: scrollAmount, behavior: 'auto' });
  }

  pauseVideoInSlide(index: number | undefined) {
    const slide = this.galleryItems?.[index as number];
    const figure = slide?.querySelector('.figure') as HTMLElement;
    if (figure) {
      //it is a video
      //pause the video
      this.videoFigure?.handleExternalVideoPause();
    }
  }
}

export default MediaGallery;
